/**
 * Presentational billing overview (ADR-0041 §11) — current plan + usage meters + feature
 * list + a pricing-table comparison. Shared by the tenant's **Abbonamento** settings
 * section AND the superadmin's per-tenant **Consumi** panel, so the operator and
 * control-plane views can't drift. Pure: takes a `BillingOverview`, renders it.
 *
 * - `variant="compact"` (the platform tenant page) shows ONLY the current-plan hero +
 *   usage meters — the plan management lives elsewhere on that page.
 * - `onSelectPlan` (the tenant settings) turns each non-current plan into a SELF-SERVE
 *   switch button (pre-Stripe — immediate + free; Stripe Checkout is Fase 1).
 */
import * as React from "react";
import { Check, Minus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";
import {
  type BillingLimitRow,
  type BillingOverview,
  type BillingPeriod,
  type BillingPlanOption,
  formatAnnualTotal,
  formatGb,
  formatPrice,
  formatPriceShort,
  hasAnnual,
} from "@/lib/api-billing";

function meterColor(ratio: number): string {
  if (ratio >= 1) return "bg-red-500";
  if (ratio >= 0.8) return "bg-amber-500";
  return "bg-primary";
}

/** A section card — subtle surface, rounded, with an optional title row. */
function Panel({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border/70 bg-surface/40">
      {title && (
        <h3 className="border-b border-border/50 px-4 py-2.5 text-sm font-semibold text-foreground">
          {title}
        </h3>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

function LimitMeter({ row }: { row: BillingLimitRow }) {
  const { t } = useTranslation();
  const unlimited = row.limit === null;
  const used = row.used;
  const ratio = unlimited || used === null || row.limit === 0 ? 0 : Math.min(used / row.limit!, 1);
  // Storage is measured in fractional GB → format adaptively (a sub-GB tenant must not read
  // as a truncated "0"). Other axes are plain counts.
  const isStorage = row.key === "max_storage_gb";
  const fmt = (v: number) => (isStorage ? formatGb(v) : v.toLocaleString());
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-muted-foreground">{t(`billing.axis.${row.key}`, row.label)}</span>
        <span className="text-sm tabular-nums text-foreground">
          <span className="font-medium">{used === null ? "—" : fmt(used)}</span>
          {unlimited ? (
            <span className="ml-1 rounded-full bg-elevated px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("billing.unlimited", "illimitato")}
            </span>
          ) : (
            <span className="text-muted-foreground"> / {fmt(row.limit!)}</span>
          )}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-elevated">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            unlimited ? "bg-border" : meterColor(ratio),
          )}
          style={{ width: unlimited ? "100%" : `${Math.max(2, Math.round(ratio * 100))}%`, opacity: unlimited ? 0.4 : 1 }}
        />
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  limits,
  features,
  period,
  currentPeriod,
  onSelect,
  busy,
}: {
  plan: BillingPlanOption;
  limits: BillingOverview["limits"];
  features: BillingOverview["features"];
  period: BillingPeriod;
  currentPeriod?: BillingPeriod; // the cadence the tenant is ACTUALLY billed on
  onSelect?: (key: string, period: BillingPeriod) => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  // A plan only honours the annual cadence if it actually offers one; otherwise show monthly.
  const effPeriod: BillingPeriod = period === "year" && hasAnnual(plan) ? "year" : "month";
  const annualTotal = effPeriod === "year" ? formatAnnualTotal(plan, t) : null;
  // "Your plan AS-IS" = same plan AND the displayed cadence matches what you're billed on.
  // Otherwise (current plan, OTHER cadence) the card is a switch target — change cadence.
  const isCurrentAsIs = plan.current && effPeriod === (currentPeriod ?? "month");
  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border p-5 transition-shadow",
        plan.current
          ? "border-primary bg-primary/[0.05] shadow-[0_0_0_1px] shadow-primary/40"
          : "border-border/70 hover:border-border",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-semibold text-foreground">{plan.name}</span>
        {plan.current && (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
            <Sparkles className="h-3 w-3" /> {t("billing.current", "Attuale")}
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-foreground">
        {formatPriceShort(plan, t, effPeriod)}
      </div>
      {effPeriod === "year" && annualTotal && (
        <p className="text-xs font-medium text-primary/80">
          {t("billing.billedAnnually", "fatturato annualmente")} · {annualTotal}
        </p>
      )}
      {plan.description && <p className="mt-1 text-xs text-muted-foreground">{plan.description}</p>}

      <ul className="mt-4 space-y-2 border-t border-border/50 pt-4 text-xs">
        {features.map((f) => (
          <li
            key={f.key}
            className={cn(
              "flex items-center gap-2",
              plan.features[f.key] ? "text-foreground" : "text-muted-foreground/50",
            )}
          >
            {plan.features[f.key] ? (
              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
            ) : (
              <Minus className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
            )}
            {t(`billing.axis.${f.key}`, f.label)}
          </li>
        ))}
        {limits.map((l) => (
          <li key={l.key} className="flex items-center justify-between gap-2 text-muted-foreground">
            <span>{t(`billing.axis.${l.key}`, l.label)}</span>
            <span className="tabular-nums font-medium text-foreground">
              {plan.limits[l.key] === null || plan.limits[l.key] === undefined
                ? "∞"
                : l.key === "max_storage_gb"
                  ? formatGb(plan.limits[l.key] as number)
                  : (plan.limits[l.key] as number).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>

      {onSelect && (
        <button
          type="button"
          disabled={isCurrentAsIs || busy}
          onClick={() => onSelect(plan.key, effPeriod)}
          className={cn(
            "mt-5 w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            isCurrentAsIs
              ? "cursor-default bg-elevated text-muted-foreground"
              : "bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-60",
          )}
        >
          {isCurrentAsIs
            ? t("billing.currentPlanShort", "Piano attuale")
            : busy
              ? t("billing.switching", "Cambio in corso…")
              : plan.current
                ? // same plan, OTHER cadence → an explicit cadence switch
                  effPeriod === "year"
                  ? t("billing.switchToYear", "Passa all'annuale")
                  : t("billing.switchToMonth", "Passa al mensile")
                : t("billing.switchTo", "Passa a questo piano")}
        </button>
      )}
    </div>
  );
}

export function BillingOverviewView({
  data,
  variant = "full",
  onSelectPlan,
  changingKey,
}: {
  data: BillingOverview;
  variant?: "full" | "compact";
  onSelectPlan?: (key: string, period: BillingPeriod) => void;
  changingKey?: string | null;
}) {
  const { t } = useTranslation();
  const compact = variant === "compact";
  // Any plan offering an annual cadence unlocks the Mensile|Annuale switch; seed it from the
  // active subscription's chosen period so the view opens on what the tenant is actually billed.
  const anyAnnual = data.plans.some((p) => p.price_year_cents != null);
  const [period, setPeriod] = React.useState<BillingPeriod>(
    data.plan?.billing_period === "year" ? "year" : "month",
  );
  const planName = data.plan
    ? data.plan.name
    : data.unlimited
      ? t("billing.noPlanUnlimited", "Illimitato (nessun abbonamento)")
      : t("billing.noPlan", "Nessun piano attivo");

  // Compact (platform tenant page): the current plan + actions already live in the
  // "Gestione abbonamento" card above, so render ONLY the usage meters — no hero, no
  // nested panel title — to avoid the triple-nested "Consumi → CURRENT PLAN → Usage".
  if (compact) {
    return (
      <div className="grid gap-x-8 gap-y-4 rounded-xl border border-border/70 bg-surface/40 p-4 sm:grid-cols-2">
        {data.limits.map((row) => (
          <LimitMeter key={row.key} row={row} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current plan hero */}
      <div className="flex flex-wrap items-end justify-between gap-4 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/[0.04] to-transparent p-5">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-widest text-primary/80">
            {t("billing.currentPlan", "Piano attuale")}
          </p>
          <h2 className="mt-1 text-2xl font-bold text-foreground">{planName}</h2>
          {data.plan?.description && (
            <p className="mt-1 text-sm text-muted-foreground">{data.plan.description}</p>
          )}
          {data.plan && (data.plan.status === "past_due" || data.plan.current_period_end) && (
            <p className="mt-1 text-xs">
              {data.plan.status === "past_due" ? (
                <span className="font-medium text-danger">
                  {t("billing.pastDue", "Pagamento in ritardo")}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {t("billing.renewsOn", "Si rinnova il")}{" "}
                  {new Date(data.plan.current_period_end as string).toLocaleDateString()}
                </span>
              )}
            </p>
          )}
        </div>
        {data.plan && (
          <div className="text-right">
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {formatPrice(
                data.plan,
                t,
                data.plan.billing_period === "year" ? "year" : "month",
              )}
            </div>
            {data.plan.billing_period === "year" && formatAnnualTotal(data.plan, t) && (
              <div className="text-xs font-medium text-primary/80">
                {formatAnnualTotal(data.plan, t)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usage meters — 2-col grid */}
      <Panel title={t("billing.usage", "Consumi")}>
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          {data.limits.map((row) => (
            <LimitMeter key={row.key} row={row} />
          ))}
        </div>
      </Panel>

      {/* Features — full variant only */}
      {!compact && (
        <Panel title={t("billing.features", "Funzionalità")}>
          <div className="flex flex-wrap gap-2">
            {data.features.map((f) => (
              <span
                key={f.key}
                title={f.description}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
                  f.included
                    ? "border-primary/30 bg-primary/10 text-foreground"
                    : "border-border/70 text-muted-foreground/70",
                )}
              >
                {f.included ? <Check className="h-3 w-3 text-primary" /> : <Minus className="h-3 w-3" />}
                {f.label}
              </span>
            ))}
          </div>
        </Panel>
      )}

      {/* Plan comparison — full variant only */}
      {!compact && data.plans.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t("billing.availablePlans", "Piani disponibili")}
            </h3>
            {anyAnnual && (
              <div
                role="group"
                aria-label={t("billing.billingPeriod", "Cadenza di fatturazione")}
                className="inline-flex rounded-lg border border-border/70 bg-surface/40 p-0.5 text-xs"
              >
                {(["month", "year"] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    className={cn(
                      "rounded-md px-3 py-1 font-medium transition-colors",
                      period === p
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {p === "month"
                      ? t("billing.monthly", "Mensile")
                      : t("billing.annual", "Annuale")}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.plans.map((p) => (
              <PlanCard
                key={p.key}
                plan={p}
                limits={data.limits}
                features={data.features}
                period={period}
                currentPeriod={data.plan?.billing_period === "year" ? "year" : "month"}
                onSelect={onSelectPlan}
                busy={changingKey === p.key}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {onSelectPlan
              ? data.stripe_managed
                ? t(
                    "billing.changeSelfHintStripe",
                    "Il cambio piano è immediato; Stripe addebita o accredita la differenza in proporzione (proration).",
                  )
                : t("billing.changeSelfHint", "Il cambio piano è immediato.")
              : t("billing.changeHint", "Per cambiare piano contatta l'amministratore della piattaforma.")}
          </p>
        </div>
      )}
    </div>
  );
}
